/**
 * MapApp.js
 * 
 * 地圖應用的核心中央管理器 (v9.1-Remaster)。
 * 持有 Leaflet 實體，並負責所有子模組 (Features) 的註冊與生命週期協調。
 */

import { bus as globalEventBus } from './sovereign-bus.js';

export class MapApp {
    constructor() {
        this.map = null;
        this.features = new Map();
        this.cabinets = new Map(); // 新增：組裝櫃存儲
        this.events = globalEventBus;
        this.bus = globalEventBus; // Alias for cabinet compatibility
        this.isReady = false;
        
        // 全域暴露以支持舊有的 JS 調用
        window.MapApp = this;
        window.appCore = this;
    }

    /**
     * 初始化/設定地圖實例
     */
    setMapInstance(leafletMap) {
        if (!leafletMap) return;
        this.map = leafletMap;
        this.isReady = true;
        window.map = leafletMap;
        
        console.log('📡 [MapApp] Core Engine Synchronized. Map instance ready.');

        // 1. 通知所有已註冊的 Feature 模組
        this.features.forEach((feature, name) => {
            if (typeof feature.onMapReady === 'function') {
                feature.onMapReady(this.map);
            }
        });

        // 2. 通知所有已註冊的 Cabinet 組裝櫃
        this.cabinets.forEach((cabinet, name) => {
            if (typeof cabinet.onMapReady === 'function') {
                cabinet.onMapReady(this.map);
            }
        });

        // 3. 廣播全域事件
        this.events.emit('map:ready', { map: this.map });
        
        // 4. 對接 Leaflet 原生事件至 SovereignBus
        this.map.on('click', (e) => {
            this.events.emit('map:click', e.latlng);
        });
    }

    /**
     * 註冊主權組裝櫃 (Cabinet)
     */
    registerCabinet(name, cabinetInstance) {
        const prev = this.cabinets.get(name);
        if (prev === cabinetInstance) return;
        if (prev?.isConnected) return;
        this.cabinets.set(name, cabinetInstance);
        console.log(`📦 [MapApp] Cabinet '${name}' docked and ready.`);
        
        if (this.isReady && typeof cabinetInstance.onMapReady === 'function') {
            cabinetInstance.onMapReady(this.map);
        }
    }

    registerFeature(name, feature) {
        if (this.features.has(name)) return;
        this.features.set(name, feature);
        if (typeof feature.init === 'function') feature.init(this);
        if (this.isReady && typeof feature.onMapReady === 'function') {
            feature.onMapReady(this.map);
        }
    }

    /**
     * [v17.5.0] 初始化 UI 佈局管理
     * 職責：負責手風琴收疊邏輯，移除 EJS 內的 Inline JS。
     * type=module 延後執行時 DOMContentLoaded 可能已過，必須用 readyState 補掛。
     */
    initUIManager() {
        if (this._uiReady) return;
        this._uiReady = true;

        const sockets = () => document.querySelectorAll('.sidebar-content > .cabinet-socket:not(.cabinet-socket-fixed)');
        sockets().forEach((el) => el.classList.add('collapsed'));

        document.addEventListener('click', (e) => {
            const header = e.target.closest('.cabinet-header');
            if (!header) return;
            const container = header.closest('.cabinet-socket');
            if (!container || !container.parentElement?.classList.contains('sidebar-content')) return;
            if (container.classList.contains('cabinet-socket-fixed')) return;
            const willOpen = container.classList.contains('collapsed');
            sockets().forEach((el) => el.classList.add('collapsed'));
            if (willOpen) container.classList.remove('collapsed');
            if (this.map) this.map.invalidateSize();
        });

        // 監聽側邊欄切換
        const toggle = document.getElementById('sidebar-toggle');
        if (toggle) {
            toggle.addEventListener('change', () => {
                setTimeout(() => {
                    if (this.map) this.map.invalidateSize();
                }, 400); // 配合 CSS transition 時間
            });
        }
    }

    getMap() { return this.map; }
    getFeature(name) { return this.features.get(name); }
    getCabinet(name) { return this.cabinets.get(name); }
}

export const appCore = new MapApp();

// 自動初始化 UI 管理器（module 延後載入時 DOM 可能已 ready）
if (typeof document !== 'undefined') {
    const bootUi = () => appCore.initUIManager();
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bootUi);
    } else {
        bootUi();
    }
}
